// Tiny localStorage cache for the last-known-good user + tenant row.
// Lets the app boot optimistically from cache while we silently re-fetch
// in the background — avoids the "Loading…" / "Account not finished" flash
// when the Supabase free-tier project is cold-starting.
//
// Keyed by auth user id so a different login can't read another user's cache.
// Cache TTL: 7 days. Anything older is ignored.

import type { User, Tenant } from './types'

const KEY = 'ss.session_cache.v1'
const TTL_MS = 7 * 24 * 60 * 60 * 1000

interface Entry {
  authUserId: string
  savedAt: number
  user: User | null
  tenant: Tenant | null
}

export function loadCachedSession(authUserId: string | null): { user: User | null; tenant: Tenant | null } | null {
  if (!authUserId) return null
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const e: Entry = JSON.parse(raw)
    if (e.authUserId !== authUserId) return null
    if (Date.now() - e.savedAt > TTL_MS) return null
    return { user: e.user, tenant: e.tenant }
  } catch { return null }
}

export function saveCachedSession(authUserId: string, user: User | null, tenant: Tenant | null): void {
  try {
    if (!authUserId) return
    const e: Entry = { authUserId, savedAt: Date.now(), user, tenant }
    localStorage.setItem(KEY, JSON.stringify(e))
  } catch { /* quota or private mode — ignore */ }
}

export function clearCachedSession(): void {
  try { localStorage.removeItem(KEY) } catch {}
}
